import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { supabase } from '../lib/supabase';
import { BarChart2, AlertCircle, Activity, Info } from 'lucide-react';
import AnalysisDisplay from '../components/AnalysisDisplay';
import AnalysisLimitMessage from '../components/AnalysisLimitMessage';
import ImageDropzone from '../components/ImageDropzone';
import { type MentorTextResponse } from '../lib/useBotAPI';

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.asistentetradingia.com';
const API_KEY = import.meta.env.VITE_API_KEY || '';

interface Analysis {
  id: string;
  created_at: string;
  image_path: string;
  result: string;
  status: 'processing' | 'completed' | 'error';
}

interface AnalysisUsage {
  total_analyses: number;
  today_analyses: number;
  remaining_analyses: number;
  limit_per_day: number;
  is_unlimited: boolean;
}

async function fetchMentorText(modo: string): Promise<MentorTextResponse> {
  const res = await fetch(`${API_BASE}/api/analisis-texto?modo=${modo}`, {
    headers: { 'X-API-Key': API_KEY },
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

const MIN_ANIMATION_MS = 3000;
const MAX_ANIMATION_MS = 5000;

const Analyses: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyses, setAnalyses] = useState<Analysis[]>([]);
  const [usage, setUsage] = useState<AnalysisUsage | null>(null);
  const [imageVersions, setImageVersions] = useState<Record<string, number>>({});
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // --- Mentor analysis state (triggered by upload) ---
  const [mentorMode, setMentorMode] = useState<'educativo' | 'experto'>('educativo');
  const [mentorSimple, setMentorSimple] = useState<string | null>(null);
  const [mentorPro, setMentorPro] = useState<string | null>(null);
  const [mentorLoading, setMentorLoading] = useState(false);
  const [mentorTimestamp, setMentorTimestamp] = useState<string | null>(null);
  const [uploadedPreview, setUploadedPreview] = useState<string | null>(null);

  const refreshImage = useCallback((analysisId: string) => {
    setImageVersions(prev => ({
      ...prev,
      [analysisId]: (prev[analysisId] || 0) + 1
    }));
  }, []);

  const setupRealtimeSubscription = useCallback(async (userId: string) => {
    if (channelRef.current) {
      await supabase.removeChannel(channelRef.current);
    }

    const channel = supabase
      .channel(`analyses_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'analyses',
          filter: `user_id=eq.${userId}`
        },
        async (payload) => {
          if (payload.new) {
            refreshImage(payload.new.id);
            setAnalyses(prevAnalyses => {
              const idx = prevAnalyses.findIndex(a => a.id === payload.new.id);
              if (idx === -1 && payload.eventType === 'INSERT') {
                return [payload.new, ...prevAnalyses];
              }
              if (idx !== -1 && payload.eventType === 'UPDATE') {
                const copy = [...prevAnalyses];
                copy[idx] = { ...copy[idx], ...payload.new };
                return copy;
              }
              return prevAnalyses;
            });
            if (payload.old?.status !== payload.new.status) {
              await fetchUsage();
            }
          }
        }
      )
      .subscribe();

    channelRef.current = channel;
  }, [refreshImage]);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        await Promise.all([fetchAnalyses(), fetchUsage()]);
        await setupRealtimeSubscription(user.id);
      } catch (err) {
        console.error('Error initializing:', err);
      }
    };
    init();
    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [setupRealtimeSubscription]);

  const fetchUsage = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase.rpc('get_user_analysis_usage', {
        user_id_param: user.id
      });
      if (error) throw error;
      setUsage(data[0]);
    } catch (err) {
      console.error('Error fetching usage:', err);
    }
  };

  const fetchAnalyses = async () => {
    try {
      setError('');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró la sesión del usuario');
      const { data, error } = await supabase
        .from('analyses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAnalyses(data || []);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los análisis');
    } finally {
      setLoading(false);
    }
  };

  // --- New upload handler: ignore image, call mentor API ---
  const handleUpload = async (file: File) => {
    try {
      setError('');
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró la sesión del usuario');

      if (usage && !usage.is_unlimited && usage.remaining_analyses <= 0) {
        throw new Error('Has alcanzado el límite diario de análisis gratuitos');
      }

      // Show preview of uploaded image
      setUploadedPreview(URL.createObjectURL(file));

      // Start animation
      setMentorLoading(true);
      setMentorSimple(null);
      setMentorPro(null);
      setMentorTimestamp(null);

      // Fetch both modes in parallel + enforce minimum animation time
      const animationStart = Date.now();
      const [simpleRes, proRes] = await Promise.all([
        fetchMentorText('educativo'),
        fetchMentorText('experto'),
      ]);

      // Ensure animation runs at least MIN_ANIMATION_MS, at most MAX_ANIMATION_MS
      const elapsed = Date.now() - animationStart;
      const remaining = Math.max(0, Math.min(MIN_ANIMATION_MS, MAX_ANIMATION_MS) - elapsed);
      if (remaining > 0) {
        await new Promise(r => setTimeout(r, remaining));
      }

      setMentorSimple(simpleRes.analysis_text);
      setMentorPro(proRes.analysis_text);
      setMentorTimestamp(simpleRes.timestamp || new Date().toISOString());
      setMentorMode('educativo');

      // Register usage in Supabase (keep tracking)
      try {
        const filePath = `${user.id}/${Date.now()}.png`;
        await supabase.storage.from('chart-images').upload(filePath, file);
        const { data: { publicUrl } } = supabase.storage
          .from('chart-images')
          .getPublicUrl(filePath);

        await supabase.from('analyses').insert([{
          user_id: user.id,
          image_path: publicUrl,
          status: 'completed',
          result: JSON.stringify({ simple: simpleRes.analysis_text, pro: proRes.analysis_text }),
        }]);
        await fetchUsage();
        await fetchAnalyses();
      } catch (trackErr) {
        console.error('Error tracking analysis:', trackErr);
        // Non-blocking — the user already has their result
      }

    } catch (err: any) {
      console.error('Error:', err);
      setError(err.message || 'Error al obtener el análisis');
    } finally {
      setMentorLoading(false);
    }
  };

  // Switch tabs — no re-fetch needed, both modes already loaded
  const handleModeSwitch = (mode: 'educativo' | 'experto') => {
    setMentorMode(mode);
  };

  const currentMentorText = mentorMode === 'educativo' ? mentorSimple : mentorPro;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Análisis de Gráficos</h1>
          <p className="mt-2 text-gray-400">
            Sube una captura de tu gráfico y el Asistente analiza el mercado por ti
          </p>
        </div>

        {/* Disclaimer NQ/MNQ */}
        <div className="bg-yellow-900/20 border border-yellow-700/40 rounded-lg px-4 py-3 flex items-start gap-3">
          <Info className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-yellow-400">
            Optimizado para <strong>NQ/MNQ</strong> (Nasdaq Futures) — otros instrumentos no soportados
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-500">{error}</p>
          </div>
        )}

        <div className="card">
          {usage && (
            <AnalysisLimitMessage
              todayAnalyses={usage.today_analyses}
              limitPerDay={usage.limit_per_day}
              remainingAnalyses={usage.remaining_analyses}
              isUnlimited={usage.is_unlimited}
            />
          )}
          <ImageDropzone
            onUpload={handleUpload}
            disabled={usage ? !usage.is_unlimited && usage.remaining_analyses <= 0 : false}
          />
        </div>

        {/* --- Mentor result (after upload) --- */}
        {(mentorLoading || currentMentorText) && (
          <div className="card">
            {/* Preview of uploaded image */}
            {uploadedPreview && (
              <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-dark-overlay mb-4">
                <img
                  src={uploadedPreview}
                  alt="Captura subida"
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            {mentorLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <Activity className="h-10 w-10 text-primary animate-pulse" />
                <p className="text-lg text-gray-300 animate-pulse">
                  Analizando por el Asistente...
                </p>
                <div className="w-48 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full animate-[progress_3s_ease-in-out_forwards]" />
                </div>
              </div>
            ) : (
              <>
                {/* Tab switcher */}
                <div className="flex items-center space-x-2 mb-3">
                  <Activity className="h-5 w-5 text-primary" />
                  <h3 className="text-lg font-semibold text-primary">Análisis del Asistente</h3>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    onClick={() => handleModeSwitch('educativo')}
                    className={`py-3 rounded-lg text-sm font-semibold transition-colors ${
                      mentorMode === 'educativo'
                        ? 'bg-primary text-white'
                        : 'bg-white/5 text-gray-400 hover:text-white'
                    }`}
                  >
                    Simple
                  </button>
                  <button
                    onClick={() => handleModeSwitch('experto')}
                    className={`py-3 rounded-lg text-sm font-semibold transition-colors ${
                      mentorMode === 'experto'
                        ? 'bg-primary text-white'
                        : 'bg-white/5 text-gray-400 hover:text-white'
                    }`}
                  >
                    Pro
                  </button>
                </div>

                {/* Analysis content */}
                {currentMentorText && (
                  <div className="bg-white/[0.03] border border-white/10 rounded-lg p-5">
                    <ReactMarkdown
                      className="prose prose-invert prose-sm max-w-none"
                      remarkPlugins={[remarkGfm]}
                    >
                      {currentMentorText}
                    </ReactMarkdown>
                  </div>
                )}

                {mentorTimestamp && (
                  <p className="text-xs text-gray-500 mt-3">
                    Datos actualizados: {new Date(mentorTimestamp).toLocaleString()}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* --- Historical analyses --- */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-white">Análisis Recientes</h2>
          {analyses.length === 0 ? (
            <div className="card text-center py-12">
              <BarChart2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-400">No hay análisis realizados aún</p>
              <p className="text-sm text-gray-500 mt-2">
                Sube tu primer gráfico para comenzar
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {analyses.map((analysis) => (
                <div key={analysis.id} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-500">
                      {new Date(analysis.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-dark-overlay mb-4">
                    <img
                      src={`${analysis.image_path}?v=${imageVersions[analysis.id] || 0}`}
                      alt="Trading Chart"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.src = 'data:image/svg+xml,';
                        img.className = 'w-full h-full p-12 bg-dark-overlay';
                      }}
                    />
                  </div>
                  <AnalysisDisplay
                    key={`${analysis.id}-${analysis.status}`}
                    id={analysis.id}
                    content={analysis.result}
                    status={analysis.status}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analyses;
